import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  message,
  Popconfirm,
  Space,
  Tag,
  Typography,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { categoryAPI } from '../services/api';

const { Text } = Typography;

interface Category {
  id: number;
  name: string;
  description?: string;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

interface CategoryManagerProps {
  onCategoryChange?: () => void;
}

const CategoryManager: React.FC<CategoryManagerProps> = ({ onCategoryChange }) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [form] = Form.useForm();

  // 显示错误弹窗
  const showErrorModal = (errorMsg: string) => {
    Modal.error({
      title: '操作失败',
      content: errorMsg,
      okText: '确定',
      icon: <ExclamationCircleOutlined />,
    });
  };

  // 验证分类名称是否已存在
  const validateCategoryName = async (name: string, excludeId?: number) => {
    if (!name || name.trim() === '') return Promise.resolve();
    
    const trimmedName = name.trim();
    const existingCategory = categories.find(cat => 
      cat.name.toLowerCase() === trimmedName.toLowerCase() && 
      cat.id !== excludeId
    );
    
    if (existingCategory) {
      return Promise.reject(new Error('分类名称已存在'));
    }
    
    return Promise.resolve();
  };

  // 获取分类列表
  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await categoryAPI.getCategories();
      if (response.data.status === 200) {
        setCategories(response.data.data);
      }
    } catch (error: any) {
      message.error(error.response?.data?.error || '获取分类列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // 当模态框打开时重置表单
  useEffect(() => {
    if (modalVisible) {
      form.resetFields();
    }
  }, [modalVisible, form]);

  // 创建分类
  const handleCreate = async (values: { name: string; description?: string }) => {
    try {
      const response = await categoryAPI.createCategory(values);
      
      if (response.data.status === 200) {
        message.success('分类创建成功');
        
        // 如果返回了默认API密钥，显示成功信息
        if (response.data.data && response.data.data.defaultApiKey) {
          message.success(`默认API密钥已创建: ${response.data.data.defaultApiKey.api_key}`);
        }
        
        setModalVisible(false);
        form.resetFields();
        fetchCategories();
        onCategoryChange?.();
      } else {
        // 处理业务逻辑错误
        const errorMsg = response.data.error || '创建分类失败';
        
        // 如果是分类名称重复的错误，显示专门的弹窗
        if (errorMsg.includes('分类名称已存在')) {
          showErrorModal('分类名称已存在，请使用其他名称');
          form.setFields([
            {
              name: 'name',
              errors: ['分类名称已存在，请使用其他名称'],
            },
          ]);
        } else {
          message.error(errorMsg);
        }
      }
    } catch (error: any) {
      const errorMsg = error.response?.data?.error || '创建分类失败';
      
      // 如果是分类名称重复的错误，显示专门的弹窗
      if (errorMsg.includes('分类名称已存在')) {
        showErrorModal('分类名称已存在，请使用其他名称');
        form.setFields([
          {
            name: 'name',
            errors: ['分类名称已存在，请使用其他名称'],
          },
        ]);
      } else {
        message.error(errorMsg);
      }
    }
  };

  // 更新分类
  const handleUpdate = async (values: { name: string; description?: string }) => {
    if (!editingCategory) return;

    try {
      const response = await categoryAPI.updateCategory(editingCategory.id, values);
      if (response.data.status === 200) {
        message.success('分类更新成功');
        setModalVisible(false);
        setEditingCategory(null);
        form.resetFields();
        fetchCategories();
        onCategoryChange?.();
      } else {
        // 处理业务逻辑错误
        const errorMsg = response.data.error || '更新分类失败';
        
        // 如果是分类名称重复的错误，显示专门的弹窗
        if (errorMsg.includes('分类名称已存在')) {
          showErrorModal('分类名称已存在，请使用其他名称');
          form.setFields([
            {
              name: 'name',
              errors: ['分类名称已存在，请使用其他名称'],
            },
          ]);
        } else {
          message.error(errorMsg);
        }
      }
    } catch (error: any) {
      const errorMsg = error.response?.data?.error || '更新分类失败';
      
      // 如果是分类名称重复的错误，显示专门的弹窗
      if (errorMsg.includes('分类名称已存在')) {
        showErrorModal('分类名称已存在，请使用其他名称');
        form.setFields([
          {
            name: 'name',
            errors: ['分类名称已存在，请使用其他名称'],
          },
        ]);
      } else {
        message.error(errorMsg);
      }
    }
  };

  // 删除分类
  const handleDelete = async (id: number) => {
    try {
      const response = await categoryAPI.deleteCategory(id);
      if (response.data.status === 200) {
        message.success('分类删除成功');
        fetchCategories();
        onCategoryChange?.();
      }
    } catch (error: any) {
      message.error(error.response?.data?.error || '删除分类失败');
    }
  };

  // 打开编辑模态框
  const openEditModal = (category: Category) => {
    setEditingCategory(category);
    form.setFieldsValue({
      name: category.name,
      description: category.description || '',
    });
    setModalVisible(true);
  };

  // 打开创建模态框
  const openCreateModal = () => {
    setEditingCategory(null);
    setModalVisible(true);
  };

  // 关闭模态框
  const closeModal = () => {
    setModalVisible(false);
    setEditingCategory(null);
  };

  // 表格列定义
  const columns = [
    {
      title: '序号',
      key: 'index',
      width: 80,
      render: (_: any, __: any, index: number) => <Text code>{index + 1}</Text>,
    },
    {
      title: '分类名称',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: Category) => {
        // 如果是默认分类，显示"默认"标签
        if (record.is_default) {
          return (
            <Tag color="blue" icon={<ExclamationCircleOutlined />}>
              默认
            </Tag>
          );
        }
        // 非默认分类也显示为标签样式
        return (
          <Tag color="green" icon={<FileTextOutlined />}>
            {name}
          </Tag>
        );
      },
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (text: string) => text || '-',
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (text: string) => new Date(text).toLocaleString(),
    },
    {
      title: '操作',
      key: 'action',
      width: 140,
      fixed: 'right' as const,
      render: (_: any, record: Category) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEditModal(record)}
            disabled={record.is_default}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个分类吗？"
            description="删除后该分类下的所有数据和秘钥都会被删除"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
            disabled={record.is_default}
          >
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
              disabled={record.is_default}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title="分类管理"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={openCreateModal}
          >
            新建分类
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={categories}
          rowKey="id"
          loading={loading}
          pagination={false}
          size="small"
          scroll={{ x: 600 }}
        />
      </Card>

      <Modal
        title={editingCategory ? '编辑分类' : '新建分类'}
        open={modalVisible}
        onCancel={closeModal}
        onOk={() => form.submit()}
        destroyOnHidden
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={editingCategory ? handleUpdate : handleCreate}
        >
          <Form.Item
            name="name"
            label="分类名称"
            rules={[
              { required: true, message: '请输入分类名称' },
              { min: 1, max: 100, message: '分类名称长度应在1-100个字符之间' },
              {
                validator: (_, value) => validateCategoryName(value, editingCategory?.id)
              }
            ]}
          >
            <Input 
              placeholder="请输入分类名称" 
            />
          </Form.Item>
          <Form.Item
            name="description"
            label="描述"
            rules={[
              { max: 500, message: '描述长度不能超过500个字符' }
            ]}
          >
            <Input.TextArea
              rows={3}
              placeholder="请输入分类描述（可选）"
              showCount
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CategoryManager;
